/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { debounceTime, filter, map } from 'rxjs/operators';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { TranslateService } from '@ngx-translate/core';
import { Site } from 'src/app/model/site';
import { CAWeekDay, CAWeeklySchedule } from 'src/app/model/control-access';
import { DatePipe } from '@angular/common';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectBoardUsersTaskRunning, selectCurrentDeviceStatus, selectWeeklyScheduleModify, selectWeeklySchedules, selectWeeklySchedulesMaxData, selectWeeklySchedulesPagination } from 'src/app/store/device/device.selector';
import { ItemsPagination, MtmaMaxData } from 'src/app/model/api/data-rest';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectDeviceTabIndex } from 'src/app/store/home/home.selector';
import { WEEKDAYS_STRING_ID, WEEKLY_SCHEDULE_DEFAULT_SORT_COLUMN, WEEKLY_SCHEDULE_DEFAULT_SORT_COLUMN_DIRECTION, WEEKLY_SCHEDULE_PAGE_DEFAULT_SIZE, WEEKLY_SCHEDULE_PAGE_SIZE_OPTIONS, WEEKLY_SCHEDULE_TABLE_HEADER } from 'src/app/conf/control-access';
import { ControlAccessWeeklyScheduleDialogComponent } from '../control-access-weekly-schedule-dialog/control-access-weekly-schedule-dialog.component';
import { DeviceStatus } from 'src/app/model/device-status';

@Component({
	selector: 'app-control-access-weekly-schedule-list',
	templateUrl: './control-access-weekly-schedule-list.component.html',
	providers: [ConfirmationService, DialogService]
})

export class ControlAccessWeeklyScheduleListComponent implements OnInit, OnDestroy {
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	weeklySchedules$: Observable<CAWeeklySchedule[] | undefined>;
	weeklySchedulesSub: Subscription | undefined;
	weeklySchedulesPagination$: Observable<ItemsPagination | undefined>;
	weeklySchedulesPaginationSub: Subscription | undefined;
	weeklySchedulesMaxData$: Observable<MtmaMaxData | undefined>;
	weeklySchedulesMaxDataSub: Subscription | undefined;
	weeklyScheduleModify$: Observable<boolean | undefined>;
	weeklyScheduleModifySub: Subscription | undefined;
	taskRunning$: Observable<boolean>;
	taskRunningSub: Subscription | undefined;
	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;
	deviceStatus: DeviceStatus;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;

	searchFilter: FormControl = new FormControl('');
	filterSub: Subscription;
	filter: '';

	weeklySchedulesColumns = WEEKLY_SCHEDULE_TABLE_HEADER;
	weeklySchedulesPagingSub: Subscription;
	weeklySchedulesPageSortSub: Subscription;
	weeklySchedulesPaging: PaginationParams = {
		size: WEEKLY_SCHEDULE_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: WEEKLY_SCHEDULE_DEFAULT_SORT_COLUMN,
		order: WEEKLY_SCHEDULE_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	weeklySchedulesPageSize: number = WEEKLY_SCHEDULE_PAGE_DEFAULT_SIZE;
	weeklySchedulesPageSizeOptions = WEEKLY_SCHEDULE_PAGE_SIZE_OPTIONS;
	totalRecords: number;

	weeklySchedulesMaxSizeMemorySub: Subscription;
	weeklySchedulesMemorySize = 1;
	weeklySchedulesMaxMemorySize = 1;

	weeklySchedulesLoading: boolean;
	weeklySchedules: CAWeeklySchedule[];

	currentSite: Site;

	weeklyScheduleConfirmDeleteSub: Subscription;
	WEEKDAYS_STRING_ID = WEEKDAYS_STRING_ID;

	dateTransform: DatePipe;

	online: boolean;
	taskRunning: boolean;


	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	opened = false;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.weeklySchedules$ = this.store.select(selectWeeklySchedules);
		this.weeklySchedulesPagination$ = this.store.select(selectWeeklySchedulesPagination);
		this.weeklySchedulesMaxData$ = this.store.select(selectWeeklySchedulesMaxData);
		this.weeklyScheduleModify$ = this.store.select(selectWeeklyScheduleModify);
		this.tabIndex$ = this.store.select(selectDeviceTabIndex);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.taskRunning$ = this.store.select(selectBoardUsersTaskRunning);
	}

	ngOnInit(): void {
		const currentLang = this.translate.currentLang;

		this.dateTransform = new DatePipe(currentLang);

		this.siteSub = this.site$.subscribe(currentSite => {
			this.currentSite = currentSite;
		});
		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				this.dispatchGetWeeklySchedules();
			});

		this.initTimeslice();
		this.onResize();
	}

	initTimeslice(): void {
		this.filterSub = this.searchFilter.valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
			this.dispatchGetWeeklySchedules();
		});
		this.dispatchGetWeeklySchedules();
		this.weeklySchedulesSub = this.weeklySchedules$.subscribe(weeklySchedules => {
			this.weeklySchedules = weeklySchedules;
			if (this.weeklySchedules !== undefined && this.weeklySchedules.length > 0) {
				for (const schedule of this.weeklySchedules) {
					schedule.WeekdaysList = this.daysOfWeekSchedule(schedule);
				}
			}
			this.weeklySchedulesLoading = false;
		});

		this.weeklySchedulesPagingSub = this.weeklySchedulesPagination$
			.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			});

		this.weeklySchedulesMaxSizeMemorySub = this.weeklySchedulesMaxData$
			.pipe(filter(p => !!p))
			.subscribe(weeklySchedulesMaxData => {
				this.weeklySchedulesMemorySize = weeklySchedulesMaxData.Current;
				this.weeklySchedulesMaxMemorySize = weeklySchedulesMaxData.Max;
			});

		this.weeklyScheduleModifySub = this.weeklyScheduleModify$
			.pipe(filter(weeklyScheduleModify => !!weeklyScheduleModify))
			.subscribe(() => {
				this.dispatchGetWeeklySchedules();
			});
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceStatus => {
			this.deviceStatus = deviceStatus;
			if (this.deviceStatus !== undefined) {
				this.online = deviceStatus.Online;
			}
		});
		this.taskRunningSub = this.taskRunning$.subscribe(taskRunning => {
			this.taskRunning = taskRunning;
		});
	}

	ngOnDestroy(): void {
		this.weeklyScheduleModifySub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.weeklySchedulesMaxSizeMemorySub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.taskRunningSub?.unsubscribe();
	}


	loadWeeklySchedules(event: TableLazyLoadEvent) {
		this.weeklySchedulesLoading = true;

		this.weeklySchedulesPaging = pageSortChange(this.weeklySchedulesPaging, event);

		this.dispatchGetWeeklySchedules();
	}

	dispatchGetWeeklySchedules(): void {
		if (this.isSelectTab()) {
			this.store.dispatch(DeviceActions.getWeeklySchedules({
				likeName: this.filter,
				sort: this.weeklySchedulesPaging.sort, order: this.weeklySchedulesPaging.order, size: this.weeklySchedulesPaging.size, page: this.weeklySchedulesPaging.page
			}));
		}
	}

	isControlAccess(): boolean {
		//return (this.deviceModel !== undefined && (this.deviceModel.Id === EDeviceModel.MTMACONNECT) && this.tabIndex === 9); // TAB INDEX FOR MTMACONNECT CALLLOG IS 9
		return true; // TAB INDEX FOR MTMACONNECT IS 7 tab only for mtma

	}

	isSelectTab(): boolean {
		return this.isControlAccess() && this.opened;
	}


	addWeeklySchedule(): void {
		this.store.dispatch(DeviceActions.resetWeeklyScheduleForm());
		this.ref = this.dialogService.open(ControlAccessWeeklyScheduleDialogComponent, {
			header: this.translate.instant('ADD_WEEKLY_SCHEDULE'),
			width: '80vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	editWeeklySchedule(weeklySchedule: CAWeeklySchedule): void {
		this.store.dispatch(DeviceActions.resetWeeklyScheduleForm());
		this.ref = this.dialogService.open(ControlAccessWeeklyScheduleDialogComponent, {
			header: this.translate.instant('EDIT_WEEKLY_SCHEDULE'),
			width: '80vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				weeklySchedule
			}
		});
	}

	deleteWeeklySchedule(weeklySchedule: CAWeeklySchedule): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_WEEKLY_SCHEDULE'),
			message: this.translate.instant('DELETE_WEEKLY_SCHEDULE_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteWeeklySchedule({ weeklyScheduleId: weeklySchedule.Id }));
			},
			reject: () => {
			}
		});
	}

	getValueMemory(): number {
		return Math.ceil((this.weeklySchedulesMemorySize / this.weeklySchedulesMaxMemorySize) * 100);
	}

	daysOfWeekSchedule(weeklySchedule: CAWeeklySchedule): CAWeekDay[] {
		const currentWeekDays = [
			{ Value: '0', Name: 'MON', Enable: false },
			{ Value: '1', Name: 'TUE', Enable: false },
			{ Value: '2', Name: 'WED', Enable: false },
			{ Value: '3', Name: 'THU', Enable: false },
			{ Value: '4', Name: 'FRI', Enable: false },
			{ Value: '5', Name: 'SAT', Enable: false },
			{ Value: '6', Name: 'SUN', Enable: false },
		];
		let value = weeklySchedule.Weekdays;
		for (let i = 6; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				currentWeekDays[i].Enable = true;
			} else {
				currentWeekDays[i].Enable = false;
			}
			value = value % (2 ** i);
		}
		return currentWeekDays;
	}

	togglePanel(opened: boolean): void {
		this.opened = opened;
		this.dispatchGetWeeklySchedules();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	dynamicInputSearchWidth(): string {
		return StandardMtmaLayout.dynamicInputSearchWidth(this.screenWidth, 'w-25rem');
	}
}